import { cn } from '../../../utils/cn';
import { money } from '../../../utils/currency';
import { masterOptions } from '../../../modules/masters/services/masterService';
import { useMasters } from '../../../modules/masters/hooks/useMasters';
import { calcLine } from '../../../lib/pricing';
import { Button } from '../../ui/Button/Button';
import { IconButton } from '../../ui/IconButton/IconButton';
import { Input, MoneyInput } from '../../ui/Input/Input';
import { Select } from '../../ui/Select/Select';
import { ItemPicker } from '../Pickers/Pickers';

let seq = 0;
/** A blank editable line. `text` is the description (labour/invoice) or name (material). */
/** One editable document line (strings for inputs, paise for money). */
export interface EditLine {
  key: string;
  itemId: string | null;
  itemLabel: string;
  text: string;
  qty: string;
  unit: string;
  rate: number | null;
  discount: number | null;
  gstRate: number;
  sacHsn: string;
  supplyBy: 'us' | 'customer';
}

export const newLine = (extra: Partial<EditLine> = {}): EditLine => ({
  key: `l${(seq += 1)}`,
  itemId: null,
  itemLabel: '',
  text: '',
  qty: '1',
  unit: 'nos',
  rate: null,
  discount: null,
  gstRate: 18,
  sacHsn: '',
  supplyBy: 'us',
  ...extra,
});

/** Saved document line -> editable line. */
export const toEditLine = (l: any, textKey = 'description'): EditLine =>
  newLine({
    itemId: l.itemId ?? null,
    itemLabel: l.name || l.description || '',
    text: l[textKey] ?? '',
    qty: String(l.qty ?? 1),
    unit: l.unit || 'nos',
    rate: l.rate ?? null,
    discount: l.discount || null,
    gstRate: l.gstRate ?? 0,
    sacHsn: l.sacHsn ?? '',
    supplyBy: l.supplyBy ?? 'us',
  });

/** Editable line -> API line. */
export const toApiLine = (l: EditLine, textKey = 'description', { supplyBy }: { supplyBy?: boolean } = {}) => ({
  ...(l.itemId ? { itemId: l.itemId } : {}),
  ...(l.text.trim() ? { [textKey]: l.text.trim() } : {}),
  qty: Number(l.qty),
  unit: l.unit || undefined,
  rate: l.rate ?? 0,
  discount: l.discount || 0,
  gstRate: Number(l.gstRate) || 0,
  ...(l.sacHsn ? { sacHsn: l.sacHsn } : {}),
  ...(supplyBy ? { supplyBy: l.supplyBy } : {}),
});

/** Returns an error message for the first bad line, or null. */
export function lineError(lines: EditLine[], { requireItem }: { requireItem?: boolean } = {}) {
  for (const [i, l] of lines.entries()) {
    if (requireItem && !l.itemId) return `Line ${i + 1}: pick a product`;
    if (!requireItem && !l.itemId && !l.text.trim()) return `Line ${i + 1}: pick an item or type a description`;
    if (!(Number(l.qty) > 0)) return `Line ${i + 1}: quantity must be more than 0`;
  }
  return null;
}

const unitOptions = (unit: string) => masterOptions('unit', { keep: unit });

const MOBILE_LABEL = 'mb-1 block text-xs text-ink-400 md:hidden';

/**
 * Table of document lines with live amounts.
 * itemType: 'service' | 'material' | undefined. requireItem: every line must be a product (purchases).
 * supplyBy: show "we supply / customer supplies" per line (quotation and job materials).
 */
export interface LineItemsEditorProps {
  lines: EditLine[];
  onChange: (lines: EditLine[]) => void;
  isGst?: boolean;
  itemType?: 'service' | 'material';
  textLabel?: string;
  /** Every line must be a product (purchases). */
  requireItem?: boolean;
  /** Show "we supply / customer supplies" per line. */
  supplyBy?: boolean;
  showDiscount?: boolean;
  addLabel?: string;
  emptyText?: string;
  rateLabel?: string;
}

export function LineItemsEditor({
  lines, onChange, isGst = true, itemType, textLabel = 'Description', requireItem, supplyBy, showDiscount = true,
  addLabel = 'Add line', emptyText = 'No lines yet.', rateLabel = 'Rate',
}: LineItemsEditorProps) {
  useMasters();
  const update = (key: string, patch: Partial<EditLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const remove = (key: string) => onChange(lines.filter((l) => l.key !== key));
  const add = () => onChange([...lines, newLine({ unit: itemType === 'service' ? 'point' : 'nos' })]);

  const pickItem = (line: EditLine, id: string | null, item: any) => {
    if (!item) return update(line.key, { itemId: null, itemLabel: '' });
    update(line.key, {
      itemId: id,
      itemLabel: item.name,
      text: requireItem ? '' : line.text || [item.name, item.brand, item.specification].filter(Boolean).join(' - '),
      unit: item.unit || line.unit,
      rate: requireItem ? item.purchaseRate ?? item.sellingRate : item.sellingRate,
      gstRate: item.gstRate ?? line.gstRate,
      sacHsn: item.sacHsn ?? '',
    });
  };

  const cols = cn(
    'md:grid md:items-start md:gap-2',
    showDiscount
      ? 'md:grid-cols-[minmax(0,1fr)_76px_92px_116px_100px_78px_104px_32px]'
      : 'md:grid-cols-[minmax(0,1fr)_76px_92px_116px_78px_104px_32px]',
  );

  return (
    <div>
      {lines.length > 0 && (
        <div className={cn(cols, 'hidden border-b border-line px-5 pb-2 text-xs font-medium uppercase tracking-wide text-ink-400')}>
          <span>{textLabel}</span>
          <span>Qty</span>
          <span>Unit</span>
          <span>{rateLabel}</span>
          {showDiscount && <span>Discount</span>}
          <span>GST</span>
          <span className="text-right">Amount</span>
          <span />
        </div>
      )}

      <ul className="divide-y divide-line">
        {lines.map((l, i) => {
          const byCustomer = supplyBy && l.supplyBy === 'customer';
          const amount = byCustomer ? 0 : calcLine({ ...l, qty: Number(l.qty) }, isGst).amount;
          return (
            <li key={l.key} className={cn(cols, 'relative grid grid-cols-2 gap-3 px-5 py-3')}>
              <div className="col-span-2 min-w-0 space-y-2 md:col-span-1">
                <span className={MOBILE_LABEL}>
                  {i + 1}. {textLabel}
                </span>
                <ItemPicker
                  type={itemType}
                  size="sm"
                  value={l.itemId}
                  selectedLabel={l.itemLabel}
                  onChange={(id, item) => pickItem(l, id, item)}
                  placeholder={requireItem ? 'Pick material' : itemType === 'service' ? 'Pick from rate card (optional)' : 'Pick from list (optional)'}
                />
                {!requireItem && (
                  <Input size="sm" aria-label={textLabel} placeholder={`${textLabel} as it should print`} value={l.text} onChange={(e) => update(l.key, { text: e.target.value })} />
                )}
                {supplyBy && (
                  <div className="flex gap-1 text-xs" role="radiogroup" aria-label="Who supplies this">
                    {[
                      ['us', 'We supply'],
                      ['customer', 'Customer supplies'],
                    ].map(([v, lab]) => (
                      <button
                        key={v}
                        type="button"
                        role="radio"
                        aria-checked={l.supplyBy === v}
                        onClick={() => update(l.key, { supplyBy: v as EditLine['supplyBy'] })}
                        className={cn(
                          'rounded-md border px-2 py-1 font-medium transition-colors',
                          l.supplyBy === v ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-ink-500 hover:border-navy-300',
                        )}
                      >
                        {lab}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <span className={MOBILE_LABEL}>Qty</span>
                <Input size="sm" aria-label="Quantity" inputMode="decimal" value={l.qty} onChange={(e) => update(l.key, { qty: e.target.value.replace(/[^\d.]/g, '') })} />
              </div>
              <div>
                <span className={MOBILE_LABEL}>Unit</span>
                <Select size="sm" aria-label="Unit" options={unitOptions(l.unit)} value={l.unit} onChange={(v) => update(l.key, { unit: v })} searchable={false} />
              </div>
              <div>
                <span className={MOBILE_LABEL}>{rateLabel}</span>
                <MoneyInput size="sm" aria-label={rateLabel} value={l.rate} onChange={(v) => update(l.key, { rate: v })} placeholder="0" disabled={byCustomer} />
              </div>
              {showDiscount && (
                <div>
                  <span className={MOBILE_LABEL}>Discount</span>
                  <MoneyInput size="sm" aria-label="Discount" value={l.discount} onChange={(v) => update(l.key, { discount: v })} placeholder="0" disabled={byCustomer} />
                </div>
              )}
              <div>
                <span className={MOBILE_LABEL}>GST</span>
                <Select size="sm" aria-label="GST rate" options={masterOptions('gstRate', { numeric: true, keep: Number(l.gstRate) })} value={Number(l.gstRate)} onChange={(v) => update(l.key, { gstRate: v })} disabled={!isGst || byCustomer} searchable={false} />
              </div>
              <div className="flex items-center justify-between md:block md:pt-2 md:text-right">
                <span className={MOBILE_LABEL}>Amount</span>
                <span className={cn('tabular text-sm font-semibold', byCustomer ? 'text-ink-400' : 'text-navy-900')}>{byCustomer ? 'By customer' : money(amount)}</span>
              </div>
              <div className="absolute right-3 top-2 md:static md:pt-0.5">
                <IconButton icon="trash" label={`Remove line ${i + 1}`} size="sm" variant="danger" onClick={() => remove(l.key)} />
              </div>
            </li>
          );
        })}
      </ul>

      {lines.length === 0 && <p className="px-5 py-6 text-center text-sm text-ink-400">{emptyText}</p>}
      <div className="border-t border-line px-5 py-3">
        <Button variant="ghost" size="sm" leftIcon="plus" onClick={add} className="-ml-2 text-brand-600">
          {addLabel}
        </Button>
      </div>
    </div>
  );
}

export default LineItemsEditor;
